import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@heroui/react/drawer';
import { useOverlayState } from '@heroui/react';
import type { SidebarProps } from './Sidebar';
import { MobileMenu } from './MobileMenu';
import { markSectionSwitch } from './nav-intent';

/**
 * The mobile navigation drawer.
 *
 * On HeroUI's Drawer, which brings the four things the old
 * translate-x `<aside>` never had: a focus trap, Escape to dismiss,
 * focus restored to the toggle on close, and `role="dialog"` +
 * `aria-modal`. The old version also stayed in the tab order while
 * visually off-screen, so keyboard users tabbed through ~60 invisible
 * links before reaching page content.
 *
 * `placement="left"` is the START edge: HeroUI pins the panel with
 * flex `justify-start`, which follows the reading direction, so the
 * Arabic menu opens from the right, beside its rail. Flipping it for
 * RTL (as this once did) put it on the wrong side. ds.css turns the
 * slide-in to match.
 */
export interface MobileDrawerProps extends Omit<
  SidebarProps,
  'collapsed' | 'inDrawer'
> {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileDrawer({
  isOpen,
  onOpenChange,
  ...sidebarProps
}: MobileDrawerProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const state = useOverlayState({ isOpen, onOpenChange });

  // Close on navigation. The drawer outlives the route change now
  // (the shell no longer remounts), so this has to be explicit.
  useEffect(() => {
    if (isOpen) onOpenChange(false);
    // Intentionally keyed on pathname alone — reacting to isOpen here
    // would slam the drawer shut the moment it opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer.Root state={state}>
      {/* The panel is nested in the scrim, as HeroUI expects. Side by side
          they were two separate overlays: a tap on the scrim never reached it
          (the panel's own full-screen layer sat on top), and once the panel
          closed the scrim stayed mounted over the page and swallowed every
          tap. The width lives on the panel, not on the layer that pins it. */}
      <Drawer.Backdrop isDismissable className="z-(--z-drawer) lg:hidden">
        <Drawer.Content placement="left">
          {/* A phone gets the whole screen, as an app's menu does (its title
              bar has the close button); a tablet keeps a side panel over the
              page, wide enough for the grouped lists. */}
          <Drawer.Dialog
            aria-label={t('nav.primary', 'Primary')}
            className="h-full w-full max-w-none bg-surface-0 p-0 sm:w-sm sm:max-w-[85vw]"
          >
            <MobileMenu
              {...sidebarProps}
              onClose={() => onOpenChange(false)}
              onNavigate={() => {
                // Picking a section from the menu swaps the screen, it does
                // not go deeper: fade, don't slide (see use-route-motion).
                markSectionSwitch();
                onOpenChange(false);
              }}
            />
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer.Root>
  );
}
